import {
    graphqlClientIntegration,
    httpClientIntegration,
    init,
    reactRouterTracingIntegration,
    setAttribute,
    setTag,
} from '@sentry/react-router';

import { env } from '~/lib/config';
import { store } from '~/store/store';

const PII_KEY_DENYLIST = ['forwarded', '-ip', 'remote-', 'via', '-user'];

/**
 * Initializes Sentry when the environment variable VITE_APP_SENTRY_ENABLED is '1' and
 * either VITE_APP_SENTRY_DSN or `sentry_frontend_dsn` value from the features API REST
 * endpoint was provided.
 */
export function sentryInit() {
    const features = store.getState().features.values;
    const dsn = features.sentry_frontend_dsn || env.VITE_APP_SENTRY_DSN;

    if (env.VITE_APP_SENTRY_ENABLED !== '1' || !dsn) {
        return;
    }

    init({
        dsn,
        release: env.VITE_APP_SENTRY_RELEASE || undefined,
        environment:
            features.sentry_environment ||
            env.VITE_APP_ENVIRONMENT ||
            undefined,
        dataCollection: {
            userInfo: false,
            cookies: false,
            httpHeaders: {
                request: { deny: PII_KEY_DENYLIST },
                response: { deny: PII_KEY_DENYLIST },
            },
            urlQueryParams: { deny: PII_KEY_DENYLIST },
        },
        beforeSend: function (event) {
            // Returning `null` means the event will not be reported

            const errorMessage =
                event.exception?.values?.[0]?.value?.toLowerCase();

            if (errorMessage?.includes('app.error.403.title')) {
                // 403 errors (permission issues) are an intentional scenario
                // which is why we will skip them.
                return null;
            }

            if (
                errorMessage?.includes(
                    'failed to fetch dynamically imported module',
                )
            ) {
                // We handle dynamic import errors in the ApplicationError class
                return null;
            }

            const frames = event.exception?.values?.[0]?.stacktrace?.frames;

            if (frames) {
                for (const frame of frames) {
                    // Frame filenames are URL paths (e.g. ../../lib/vsphere/wmks.min.js),
                    // so match on the file's base name, not the full path.
                    const frameBaseName = frame?.filename?.split('/').pop();

                    if (
                        [
                            // Ignore errors in wmks and guacamole modules as they
                            // are third-party
                            'wmks',
                            'guacamole',
                        ].some(
                            (ignoredChunkBaseName) =>
                                frameBaseName?.startsWith(
                                    ignoredChunkBaseName,
                                ) ?? false,
                        )
                    ) {
                        return null;
                    }
                }
            }

            return event;
        },
        integrations: [
            httpClientIntegration(),
            graphqlClientIntegration({
                endpoints: [`${env.VITE_API_BASE_URL}/graphql`],
            }),
            reactRouterTracingIntegration(),
        ],
        tracesSampleRate: env.VITE_APP_SENTRY_TRACES_SAMPLE_RATE
            ? parseFloat(env.VITE_APP_SENTRY_TRACES_SAMPLE_RATE)
            : 0,
        tracePropagationTargets: [env.VITE_APP_BASE_URL, env.VITE_API_BASE_URL],
    });

    setTag('service', 'smartlearn-app');
    setAttribute('service', 'smartlearn-app');
}
